import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { skillsAPI } from '../../services/api';

// Асинхронные экшены
export const fetchSkills = createAsyncThunk(
  'skills/fetchSkills',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await skillsAPI.getAll(params);
      return {
        data: response.data,
        params
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSkillGroups = createAsyncThunk(
  'skills/fetchSkillGroups',
  async (params = { all: true }, { rejectWithValue }) => {
    try {
      const response = await skillsAPI.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchSkillById = createAsyncThunk(
  'skills/fetchSkillById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await skillsAPI.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const skillsSlice = createSlice({
  name: 'skills',
  initialState: {
    items: [],
    groups: [], // Отдельный массив для групп навыков
    selectedSkill: null,
    loading: false,
    groupsLoading: false,
    error: null,
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      pages: 1
    }
  },
  reducers: {
    // Синхронное обновление навыка (используется при получении событий)
    updateSkill: (state, action) => {
      const skill = action.payload;
      const index = state.items.findIndex(item => item.id === skill.id);

      if (index !== -1) {
        // Обновление существующего навыка
        state.items[index] = {
          ...state.items[index],
          ...skill,
        };
      } else {
        // Добавление нового навыка
        state.items.push(skill);
      }

      // Также обновляем в группах, если навык там есть
      const groupIndex = state.groups.findIndex(item => item.id === skill.id);
      if (groupIndex !== -1) {
        state.groups[groupIndex] = {
          ...state.groups[groupIndex],
          ...skill,
        };
      } else {
        // Добавляем новый навык в список групп, если его там нет
        state.groups.push(skill);
      }

      // Если открыта детальная страница навыка, обновляем и её
      if (state.selectedSkill && state.selectedSkill.id === skill.id) {
        state.selectedSkill = {
          ...state.selectedSkill,
          ...skill,
        };
      }
    },

    // Удаление навыка (используется при получении событий)
    deleteSkill: (state, action) => {
      const id = action.payload;
      // Преобразуем id к строке для надежного сравнения
      const idStr = id?.toString();

      if (!idStr) {
        console.error('Редьюсер: получен пустой ID для удаления навыка');
        return;
      }

      // Фильтруем элементы, сравнивая строковые представления ID
      state.items = state.items.filter(item => {
        const itemId = item.id?.toString();
        const result = itemId !== idStr;
        if (!result) {
          console.log(`Редьюсер: удаляем навык с ID ${itemId}`);
        }
        return result;
      });

      // Также удаляем из групп
      state.groups = state.groups.filter(item => {
        const itemId = item.id?.toString();
        return itemId !== idStr;
      });

      // Если открыта детальная страница удаляемого навыка, очищаем её
      if (state.selectedSkill && state.selectedSkill.id?.toString() === idStr) {
        state.selectedSkill = null;
      }
    },

    // Сброс выбранного навыка
    resetSelectedSkill: (state) => {
      state.selectedSkill = null;
    },

    // Обновление параметров пагинации
    setPagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Обработка fetchSkills
      .addCase(fetchSkills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSkills.fulfilled, (state, action) => {
        const { data, params } = action.payload;

        // Обработка разных форматов ответа API
        if (data && data.data && Array.isArray(data.data)) {
          // Новый формат с вложенными данными и пагинацией
          state.items = data.data;

          // Обновляем пагинацию, если она есть
          if (data.pagination) {
            state.pagination = {
              page: params.page || 1,
              limit: params.limit || 10,
              total: data.pagination.total || 0,
              pages: Math.ceil(data.pagination.total / (params.limit || 10)) || 1
            };
          }
        } else if (Array.isArray(data)) {
          // Старый формат - прямой массив
          state.items = data;
          state.pagination = {
            ...state.pagination,
            total: data.length,
            pages: Math.ceil(data.length / (params.limit || 10)) || 1
          };
        }

        state.loading = false;
      })
      .addCase(fetchSkills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Не удалось загрузить навыки';
      })

      // Обработка fetchSkillGroups
      .addCase(fetchSkillGroups.pending, (state) => {
        state.groupsLoading = true;
        state.error = null;
      })
      .addCase(fetchSkillGroups.fulfilled, (state, action) => {
        const data = action.payload;

        // Обработка разных форматов ответа API
        if (data && data.data && Array.isArray(data.data)) {
          state.groups = data.data;
        } else if (Array.isArray(data)) {
          state.groups = data;
        } else {
          state.groups = [];
        }

        state.groupsLoading = false;
      })
      .addCase(fetchSkillGroups.rejected, (state, action) => {
        state.groupsLoading = false;
        state.error = action.payload || 'Не удалось загрузить группы навыков';
      })

      // Обработка fetchSkillById
      .addCase(fetchSkillById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSkillById.fulfilled, (state, action) => {
        state.selectedSkill = action.payload;
        state.loading = false;
      })
      .addCase(fetchSkillById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Не удалось загрузить навык';
      });
  },
});

// Экспорт экшенов
export const {
  updateSkill,
  deleteSkill,
  resetSelectedSkill,
  setPagination
} = skillsSlice.actions;

// Селекторы
export const selectAllSkills = (state) => state.skills.items;
export const selectSkillGroups = (state) => state.skills.groups;
export const selectSelectedSkill = (state) => state.skills.selectedSkill;
export const selectSkillsLoading = (state) => state.skills.loading;
export const selectSkillGroupsLoading = (state) => state.skills.groupsLoading;
export const selectSkillsError = (state) => state.skills.error;
export const selectSkillsPagination = (state) => state.skills.pagination;

// Экспорт редьюсера
export default skillsSlice.reducer;
